import React, { useState, useEffect, ChangeEvent, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import IndexHeader from "app/shared-components/IndexHeader";
import { reportService } from "src/app/services/api/report.service";
import { downloadService } from "src/app/services/api/download.service";
import { YouthRegistrationRaw } from "src/app/@types/interfaces/report.interface";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#202441",
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const AdminYouthRegistrations: React.FC = () => {
  const [data, setData] = useState<YouthRegistrationRaw[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    program: "",
    from: "",
    to: "",
  });
  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (filters.program && r["Program"] !== filters.program) return false;

      if (filters.from && filters.from.trim() !== "") {
        const regDate = new Date(r["Registration Date"]);
        const fromDate = new Date(filters.from);
        fromDate.setHours(0, 0, 0, 0);
        if (
          isNaN(regDate.getTime()) ||
          isNaN(fromDate.getTime()) ||
          regDate < fromDate
        )
          return false;
      }

      if (filters.to && filters.to.trim() !== "") {
        const regDate = new Date(r["Registration Date"]);
        const toDate = new Date(filters.to);
        toDate.setHours(23, 59, 59, 999);
        if (
          isNaN(regDate.getTime()) ||
          isNaN(toDate.getTime()) ||
          regDate > toDate
        )
          return false;
      }

      return true;
    });
  }, [data, filters]);

  const paginatedData = useMemo(() => {
    return filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const resp = await reportService.getTotalYouthRegistrationsByProgram();

      if (resp.success && resp.data?.registrations) {
        const rawData = resp.data.registrations as YouthRegistrationRaw[];
        setData(rawData);
      } else {
        throw new Error(resp.message || "Failed to load data");
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const handleFilterChange =
    (field: keyof typeof filters) =>
    (
      e:
        | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<string>
    ) => {
      setFilters((f) => ({
        ...f,
        [field]: e.target.value,
      }));
      setPage(0);
    };

  const handleClear = () => {
    setFilters({ program: "", from: "", to: "" });
    setPage(0);
  };

  const handleGenerate = () => {
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const downloadExcel = () => {
    const filename = "Total_Youth_Registrations_by_Program.xlsx";
    const filepath = `${process.env.PUBLIC_URL}/assets/data/Total Youth Registrations by Program.xlsx`;
    downloadService.downloadFileWithPath(filename, filepath);
  };

  const downloadPDF = () => {
    const filename = "Total_Youth_Registrations_by_Program.pdf";
    const filepath = `${process.env.PUBLIC_URL}/assets/data/Total_Youth_Registrations_by_Program.pdf`;
    downloadService.downloadFileWithPath(filename, filepath);
  };

  if (loading) {
    return (
      <>
        <IndexHeader
          name="Total Youth Registrations by Program"
          update={false}
          add={false}
          redirectingPath="/reports/total-youth-registrations"
        />
        <Box m={4} textAlign="center">
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <IndexHeader
          name="Total Youth Registrations by Program"
          update={false}
          add={false}
          redirectingPath="/reports/total-youth-registrations"
        />
        <Box m={4} textAlign="center">
          <Typography color="error">{error}</Typography>
        </Box>
      </>
    );
  }

  const programs = Array.from(new Set(data.map((r) => r["Program"]))).filter(
    Boolean
  );

  return (
    <>
      <IndexHeader
        name="Total Youth Registrations by Program"
        update={false}
        add={false}
        redirectingPath="/reports/total-youth-registrations"
      />

      <Box m={3}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Program</InputLabel>
              <Select
                value={filters.program}
                label="Program"
                onChange={handleFilterChange("program")}
              >
                <MenuItem value="">All Programs</MenuItem>
                {programs.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item>
            <TextField
              label="From"
              type="date"
              size="small"
              value={filters.from}
              onChange={handleFilterChange("from")}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: filters.to || undefined }}
              sx={{ width: 180 }}
            />
          </Grid>

          <Grid item>
            <TextField
              label="To"
              type="date"
              size="small"
              value={filters.to}
              onChange={handleFilterChange("to")}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: filters.from || undefined }}
              sx={{ width: 180 }}
            />
          </Grid>

          <Grid item>
            <Button variant="outlined" onClick={handleClear}>
              Clear All
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={handleGenerate}>
              Generate
            </Button>
          </Grid>
        </Grid>

        <Box mt={2} display="flex" gap={2}>
          <Button variant="contained" onClick={downloadPDF}>
            Download (PDF)
          </Button>
          <Button variant="contained" onClick={downloadExcel}>
            Download (Excel)
          </Button>
        </Box>
      </Box>

      <Box m={3}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>Youth ID</StyledTableCell>
                <StyledTableCell>Full Name</StyledTableCell>
                <StyledTableCell>Address</StyledTableCell>
                <StyledTableCell>Program</StyledTableCell>
                <StyledTableCell>Registration Date</StyledTableCell>
                <StyledTableCell>School</StyledTableCell>
                <StyledTableCell>Grade</StyledTableCell>
                <StyledTableCell>Gender</StyledTableCell>
                <StyledTableCell>Age</StyledTableCell>
                <StyledTableCell>Host Organization</StyledTableCell>
                <StyledTableCell>Start Date</StyledTableCell>
                <StyledTableCell>Completion Date</StyledTableCell>
                <StyledTableCell align="right">
                  Total Salary (USD)
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((r) => (
                  <TableRow key={r["Youth ID"]}>
                    <StyledTableCell>{r["Youth ID"]}</StyledTableCell>
                    <StyledTableCell>{r["Full Name"]}</StyledTableCell>
                    <StyledTableCell>{r["Ward"] || "N/A"}</StyledTableCell>
                    <StyledTableCell>{r["Program"]}</StyledTableCell>
                    <StyledTableCell>{r["Registration Date"]}</StyledTableCell>
                    <StyledTableCell>{r["School"]}</StyledTableCell>
                    <StyledTableCell>{r["Grade"]}</StyledTableCell>
                    <StyledTableCell>{r["Gender"]}</StyledTableCell>
                    <StyledTableCell>{r["Age"]}</StyledTableCell>
                    <StyledTableCell>
                      {r.hostOrganization || "N/A"}
                    </StyledTableCell>
                    <StyledTableCell>{r.startDate || "N/A"}</StyledTableCell>
                    <StyledTableCell>
                      {r.completionDate || "N/A"}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      {r.totalSalary ? `$${r.totalSalary.toFixed(2)}` : "$0.00"}
                    </StyledTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={13} align="center">
                    No records found.
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Box>
    </>
  );
};

export default AdminYouthRegistrations;
